import { useAddInStore, useTokenStore } from "../hooks";
import { Token } from "../models/authentication";
import { logger } from "../utils";
import { envUtils } from "../utils/envUtils";
import { createLocalUrl } from "../utils/helpers";

interface DialogMessage {
  message: string;
  origin: string;
}

type LoginResponse = {
  error?: any;
  token?: Token;
};

type ExchangeTokenParams = {
  code: string;
  redirect_uri: string;
};

type TokenResponse = {
  token?: Token;
  error?: any;
  status: number;
  success: boolean;
};

type StartOnboardingOptions = {
  url?: string;
  onComplete?: (msg: DialogMessage) => void;
  onError?: (msg: Office.Error) => void;
};

/**
 * Logs the user in using Auth0 and opens a dialog for authentication.
 *
 * @returns {Promise<LoginResponse>} - A promise that resolves with the login response
 * @throws {Error} - Throws an error if the login process fails
 */
export const loginWithAuth0 = (): Promise<LoginResponse> => {
  return new Promise((resolve, reject) => {
    try {
      const dialogUrl = new URL(`https://${envUtils.AUTH0_DOMAIN}/authorize`);
      dialogUrl.searchParams.append("response_type", "code");
      dialogUrl.searchParams.append("client_id", envUtils.AUTH0_CLIENT_ID);
      dialogUrl.searchParams.append("redirect_uri", createLocalUrl("auth.html"));
      dialogUrl.searchParams.append("scope", "openid offline_access profile email");
      dialogUrl.searchParams.append("audience", envUtils.AUTH0_AUDIENCE);
      dialogUrl.searchParams.append("state", `cadchat-ms-office-addin-${Math.random()}`);

      /** indicates that a dialog is open in the application */
      useAddInStore.setState({ isDialogOpen: true });

      /** create and open the dialog */
      Office.context.ui.displayDialogAsync(
        dialogUrl.toString(),
        {
          height: 60,
          width: 30,
          displayInIframe: false,
          promptBeforeOpen: false,
        },
        function (result: Office.AsyncResult<Office.Dialog>) {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value;

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (msg: DialogMessage) => {
              try {
                // safe parse the message
                const authResult = JSON.parse(msg.message);

                // validate the auth result for errors or invalid states
                if (authResult?.error) {
                  reject({
                    error: authResult?.error ?? new Error(`Unable to complete login`),
                    ...authResult,
                  });
                } else if (!authResult?.id_token || !authResult?.refresh_token) {
                  reject({
                    error: new Error("Authentication token missing"),
                    ...authResult,
                  });
                } else {
                  resolve({
                    token: authResult as Token,
                  });
                }
              } catch (er) {
                reject({ error: new Error("Error parsing dialog message") });
              } finally {
                // close the dialog
                dialog.close();

                // make sure we set the dialog state to closed
                useAddInStore.setState({ isDialogOpen: false });
              }
            });
          } else {
            reject(result.error);

            // make sure we set the dialog state to closed
            useAddInStore.setState({ isDialogOpen: false });
          }
        }
      );
    } catch (error) {
      // make sure we set the dialog state to closed
      useAddInStore.setState({ isDialogOpen: false });

      // bubble up the error
      throw error;
    }
  });
};

/**
 * Log the user out of Auth0 and close the dialog.
 *
 * @returns {Promise<void>} - A promise that resolves when the logout process is complete
 * @throws {Error} - Throws an error if the logout process fails
 */
export const logoutFromAuth0 = async () => {
  return new Promise((resolve, reject) => {
    try {
      const logoutUrl = new URL(`https://${envUtils.AUTH0_DOMAIN}/v2/logout`);
      logoutUrl.searchParams.append("client_id", envUtils.AUTH0_CLIENT_ID);
      logoutUrl.searchParams.append("returnTo", `${createLocalUrl("auth.html")}?is_logout=true`);

      /** indicates that a dialog is open in the application */
      useAddInStore.setState({ isDialogOpen: true });

      /** create and open dialog */
      Office.context.ui.displayDialogAsync(
        logoutUrl.toString(),
        {
          height: 60,
          width: 30,
          displayInIframe: false,
          promptBeforeOpen: false,
        },
        function (result: Office.AsyncResult<Office.Dialog>) {
          // handle the result status
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value;

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (msg: DialogMessage) => {
              resolve(msg);
              dialog.close();

              // make sure we set the dialog state to closed
              useAddInStore.setState({ isDialogOpen: false });
            });
          } else {
            reject(result.error);

            // make sure we set the dialog state to closed
            useAddInStore.setState({ isDialogOpen: false });
          }
        }
      );
    } catch (error) {
      // make sure we set the dialog state to closed
      useAddInStore.setState({ isDialogOpen: false });

      // bubble up the error
      throw error;
    }
  });
};

/**
 * Exchanges the Auth0 authorization code for an access token.
 *
 * @param param0 - The parameters for the token exchange
 * @param param0.code - The authorization code received from the Auth0 authorization endpoint
 * @param param0.redirect_uri - The redirect URI used in the authorization request
 *
 * @returns {Promise<Token>} - The token object containing the access token, ID token, and refresh token
 * @throws {Error} - Throws an error if the token exchange fails
 */
export const exchangeCodeForToken = async ({
  code,
  redirect_uri,
}: ExchangeTokenParams): Promise<Token> => {
  const response = await fetch(`https://${envUtils.AUTH0_DOMAIN}/oauth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      grant_type: "authorization_code",
      client_id: envUtils.AUTH0_CLIENT_ID,
      client_secret: envUtils.AUTH0_CLIENT_SECRET,
      code,
      redirect_uri,
    }),
  });

  // attempt to parse the response as JSON
  const data = await response.json();

  // check if the response is ok
  if (!response.ok) {
    const errorMessage = data.error_description ?? data.error;
    throw new Error(errorMessage ?? "Token exchange failed");
  }

  return data as Token;
};

/**
 * Refreshes the Auth0 token using the refresh token.
 *
 * @param refreshToken - The refresh token to use for refreshing the access token
 * @returns {TokenResponse} - A response object containing the new token, error message, status code, and success flag
 * @throws {Error} - Throws an error if the refresh token is not found
 */
export const refreshAuth0Token = async (): Promise<TokenResponse> => {
  const refreshToken = useTokenStore.getState().token?.refresh_token;
  if (!refreshToken) {
    return {
      token: undefined,
      error: {
        error_description: "No refresh token found",
      },
      status: 500,
      success: false,
    };
  }

  const searchParams = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: envUtils.AUTH0_CLIENT_ID,
    client_secret: envUtils.AUTH0_CLIENT_SECRET,
    audience: envUtils.AUTH0_AUDIENCE,
    scope: "openid offline_access profile email",
    refresh_token: refreshToken,
  });

  const response = await fetch(`https://${envUtils.AUTH0_DOMAIN}/oauth/token`, {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
    body: searchParams.toString(),
  });

  const data = await response.clone().json();

  return {
    token: response.ok ? (data as Token) : undefined,
    error: !response.ok ? data.error : undefined,
    status: response.status,
    success: response.ok,
  };
};

/**
 * Starts the onboarding process for the CADChat application.
 *
 * @param onboardingUrl - The URL to open in the dialog (optional)
 * @returns {Promise<DialogMessage>} - A promise that resolves with the dialog message
 * @throws {Error} - Throws an error if the onboarding process fails
 */
export const startCadChatOnboardingProcess = (onboardingUrl?: string): Promise<Office.Dialog> => {
  return new Promise((resolve, reject) => {
    try {
      const dialogUrl =
        envUtils.RELEASE_TYPE === "development"
          ? `${createLocalUrl("modal.html")}?redirect_to=${encodeURIComponent(onboardingUrl)}`
          : onboardingUrl;

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 60, width: 40, promptBeforeOpen: false },
        function (result: Office.AsyncResult<Office.Dialog>) {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value;

            /** indicates that a dialog is open in the application */
            useAddInStore.setState({ isDialogOpen: true });

            resolve(dialog);
          } else {
            reject(result.error);
          }
        }
      );
    } catch (error) {
      logger.error("Error starting onboarding process:", error);
      reject(error);
    }
  });
};

/**
 * Starts the onboarding process for the CADChat application.
 *
 * @param onboardingUrl - The URL to open in the dialog (optional)
 * @returns {Promise<DialogMessage>} - A promise that resolves with the dialog message
 * @throws {Error} - Throws an error if the onboarding process fails
 */
export const showGettingStartedModal = (): Promise<Office.Dialog> => {
  return new Promise((resolve, reject) => {
    try {
      const dialogUrl = `${createLocalUrl("modal.html")}?is_help_modal=true`;

      Office.context.ui.displayDialogAsync(
        dialogUrl,
        { height: 60, width: 40, promptBeforeOpen: false },
        function (result: Office.AsyncResult<Office.Dialog>) {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = result.value;

            /** indicates that a dialog is open in the application */
            useAddInStore.setState({ isDialogOpen: true });

            resolve(dialog);
          } else {
            reject(result.error);
          }
        }
      );
    } catch (error) {
      logger.error("Error opening help modal:", error);
      reject(error);
    }
  });
};
